import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { supabase } from "@/platform/lib/supabase";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

/**
 * Handler de callback de auth: chamada após email-confirm, magic-link ou OAuth.
 * Supabase já popula a sessão via getSession() automaticamente quando o hash
 * (#access_token=...) é detectado. Aqui apenas roteamos de acordo com:
 * - Se há um `code` query (OAuth flow), troca por sessão via exchangeCodeForSession.
 * - Se há um `type=recovery`, manda pra /redefinir.
 * - Caso normal: aguarda sessão e vai pra /onboarding ou /painel.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"working" | "error">("working");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. PKCE code flow (OAuth, magic link com PKCE)
        const code = params.get("code");
        if (code) {
          const { error: exchErr } = await supabase.auth.exchangeCodeForSession(code);
          if (exchErr) throw exchErr;
        }

        // 2. Type=recovery → vai pra redefinir senha
        const type = params.get("type");
        if (type === "recovery") {
          navigate("/redefinir", { replace: true });
          return;
        }

        // 3. Aguarda sessão estabelecer (em geral imediato após exchange)
        let attempts = 0;
        let session = (await supabase.auth.getSession()).data.session;
        while (!session && attempts < 10) {
          await new Promise((r) => setTimeout(r, 200));
          session = (await supabase.auth.getSession()).data.session;
          attempts += 1;
        }

        if (cancelled) return;

        if (!session) {
          throw new Error("Sessão não estabelecida. Tente entrar novamente.");
        }

        // 4. Detecta intenção de tenant (do query param ou user_metadata).
        const tenantParam = params.get("tenant");
        const intentTenant =
          (tenantParam === "usp" || tenantParam === "einstein"
            ? tenantParam
            : (session.user.user_metadata?.intent_tenant as string | undefined)) ?? "einstein";

        // 5. Existe assinatura ativa (active|trialing) nesse tenant?
        const { data: subs } = await supabase
          .from("platform_subscriptions")
          .select("status, tenant")
          .eq("user_id", session.user.id)
          .in("status", ["active", "trialing", "past_due"]);

        const hasIntentSub = (subs ?? []).some((s) => s.tenant === intentTenant);
        if (!hasIntentSub) {
          // Sem assinatura no tenant pretendido → cartão obrigatório.
          toast.success("Conta confirmada. Escolha seu plano para começar.");
          navigate(`/precos?tenant=${intentTenant}`, { replace: true });
          return;
        }

        // 6. Tem assinatura — checa onboarding do tenant.
        if (intentTenant === "usp") {
          const { data: usp } = await supabase
            .from("usp_profiles")
            .select("onboarding_completed")
            .eq("user_id", session.user.id)
            .maybeSingle();
          if (!usp?.onboarding_completed) {
            toast.success("Tudo pronto. Vamos calibrar seu plano Fuvest.");
            navigate("/usp/onboarding", { replace: true });
          } else {
            toast.success("Bem-vinda de volta.");
            navigate("/usp/painel", { replace: true });
          }
          return;
        }

        const { data: profile } = await supabase
          .from("einstein_profiles")
          .select("onboarding_completed")
          .eq("id", session.user.id)
          .maybeSingle();
        if (!profile?.onboarding_completed) {
          toast.success("Tudo pronto. Vamos calibrar seu plano.");
          navigate("/onboarding", { replace: true });
        } else {
          toast.success("Bem-vinda de volta.");
          navigate("/painel", { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Falha no callback de autenticação.");
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />
      <Container size="default" className="relative flex min-h-[calc(100vh-56px)] flex-col items-center justify-center text-center">
        {phase === "working" ? (
          <>
            <Badge variant="accentSolid">Confirmando…</Badge>
            <Loader2
              className="mt-6 size-10 animate-spin text-[color:var(--color-accent)]"
              aria-hidden
            />
            <h1 className="mt-6 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Validando sua sessão…
            </h1>
            <p className="mt-3 max-w-md text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              Em instantes você é redirecionada pro próximo passo.
            </p>
          </>
        ) : (
          <>
            <Badge variant="danger">Erro</Badge>
            <h1 className="mt-6 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Não consegui confirmar.
            </h1>
            <p className="mt-3 max-w-md text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              {error ?? "Tente abrir o link novamente ou entre com email e senha."}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button size="lg" variant="gradient" onClick={() => navigate("/entrar", { replace: true })}>
                Ir para entrar
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/", { replace: true })}>
                Voltar ao início
              </Button>
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
